import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AIIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import moderationConfigService from '../../services/moderationConfigService';

const ModerationConfig = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    // Text validation
    min_title_length: 10,
    min_description_length: 20,
    // Image validation
    max_image_size_mb: 5.0,
    min_image_resolution: 500,
    // Auto-moderation thresholds
    auto_approve_confidence: 0.90,
    // AI detection features
    nsfw_detection_enabled: true,
    nsfw_threshold: 0.60,
    watermark_detection_enabled: true,
    watermark_threshold: 0.70,
    // Text Analysis features
    profanity_detection_enabled: true,
    profanity_threshold: 0.50,
    language_detection_enabled: true,
    grammar_checking_enabled: true,
    grammar_threshold: 0.50,
    // ML Model features
    ml_enabled: false,
    ml_confidence_threshold: 0.80,
    ml_auto_training_enabled: true,
    ml_training_interval_days: 7,
    ml_min_training_samples: 100,
    // Admin Override & Learning
    admin_override_learning_enabled: true,
    // AI-powered Auto-moderation
    ai_moderation_enabled: true,
    ai_auto_approval_enabled: false,
    ai_auto_approval_threshold: 0.85,
    ai_auto_rejection_enabled: true,
    // Image quality thresholds
    min_brightness: 50,
    max_brightness: 220,
    min_contrast: 20,
    // Penalty system
    max_penalties: 10,
  });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await moderationConfigService.getConfig();
      if (response.success && response.config) {
        setConfig(response.config);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      showAlert('Failed to load configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await moderationConfigService.updateConfig(config);
      if (response.success) {
        showAlert('Configuration saved successfully', 'success');
        if (response.config) {
          setConfig(response.config);
        }
      }
    } catch (error) {
      console.error('Error saving config:', error);
      showAlert(error.response?.data?.error || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
  };

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: theme.palette.background.default,
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden'
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.palette.background.default,
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={{ xs: 2, sm: 3 }}
          gap={{ xs: 2, sm: 0 }}
        >
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom>
              <SettingsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Moderation Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
              Configure AI moderation, ML models, and auto-detection settings
            </Typography>
          </Box>
        <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={!isMobile && <RefreshIcon />}
            onClick={fetchConfig}
            disabled={saving}
            size={isMobile ? "small" : "medium"}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={!isMobile && <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size={isMobile ? "small" : "medium"}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }} onClose={() => setAlert({ show: false, message: '', severity: 'success' })}>
          {alert.message}
        </Alert>
      )}

      {/* AI Moderation Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="primary" />
            <Typography variant="h6">AI-Powered Moderation</Typography>
            <Chip 
              label={config.ai_moderation_enabled ? "Enabled" : "Disabled"} 
              color={config.ai_moderation_enabled ? "success" : "default"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.ai_moderation_enabled}
                    onChange={(e) => handleChange('ai_moderation_enabled', e.target.checked)}
                  />
                }
                label="Enable AI-Powered Moderation (NSFW, Watermark, Text Analysis)"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.ai_auto_approval_enabled}
                    onChange={(e) => handleChange('ai_auto_approval_enabled', e.target.checked)}
                    disabled={!config.ai_moderation_enabled}
                  />
                }
                label="Allow AI Auto-Approval"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Automatically approve products that pass all AI checks with high confidence
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="AI Auto-Approval Threshold"
                value={config.ai_auto_approval_threshold}
                onChange={(e) => handleChange('ai_auto_approval_threshold', parseFloat(e.target.value))}
                disabled={!config.ai_auto_approval_enabled}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                helperText="Confidence threshold for AI auto-approval (0.0 - 1.0)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.ai_auto_rejection_enabled}
                    onChange={(e) => handleChange('ai_auto_rejection_enabled', e.target.checked)}
                    disabled={!config.ai_moderation_enabled}
                  />
                }
                label="Allow AI Auto-Rejection"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Automatically reject products with NSFW content, watermarks, or profanity
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* NSFW Detection Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <ShieldIcon color="error" />
            <Typography variant="h6">NSFW Detection</Typography>
            <Chip 
              label={config.nsfw_detection_enabled ? "Enabled" : "Disabled"} 
              color={config.nsfw_detection_enabled ? "success" : "default"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.nsfw_detection_enabled}
                    onChange={(e) => handleChange('nsfw_detection_enabled', e.target.checked)}
                  />
                }
                label="Enable NSFW Content Detection (NudeNet)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Detect and flag inappropriate/adult content in product images
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography gutterBottom>
                NSFW Detection Threshold: {config.nsfw_threshold}
              </Typography>
              <Slider
                value={config.nsfw_threshold}
                onChange={(e, value) => handleChange('nsfw_threshold', value)}
                disabled={!config.nsfw_detection_enabled}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 0.5, label: '50%' },
                  { value: 1, label: '100%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
              />
              <Typography variant="caption" color="text.secondary">
                Lower threshold = more sensitive (may have false positives)
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Watermark Detection Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <ImageIcon color="warning" />
            <Typography variant="h6">Watermark Detection</Typography>
            <Chip 
              label={config.watermark_detection_enabled ? "Enabled" : "Disabled"} 
              color={config.watermark_detection_enabled ? "success" : "default"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.watermark_detection_enabled}
                    onChange={(e) => handleChange('watermark_detection_enabled', e.target.checked)}
                  />
                }
                label="Enable Watermark Detection (OpenCV)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Detect watermarks in product images (prevents stolen/branded content)
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography gutterBottom>
                Watermark Detection Threshold: {config.watermark_threshold}
              </Typography>
              <Slider
                value={config.watermark_threshold}
                onChange={(e, value) => handleChange('watermark_threshold', value)}
                disabled={!config.watermark_detection_enabled}
                min={0}
                max={1}
                step={0.05}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 0.5, label: '50%' },
                  { value: 1, label: '100%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Text Analysis Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TextFieldsIcon color="info" />
            <Typography variant="h6">Text Analysis</Typography>
            <Chip 
              label={config.profanity_detection_enabled ? "Enabled" : "Disabled"} 
              color={config.profanity_detection_enabled ? "success" : "default"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Profanity Detection */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Profanity Detection
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.profanity_detection_enabled}
                    onChange={(e) => handleChange('profanity_detection_enabled', e.target.checked)}
                  />
                }
                label="Enable Profanity Detection"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Profanity Threshold"
                value={config.profanity_threshold}
                onChange={(e) => handleChange('profanity_threshold', parseFloat(e.target.value))}
                disabled={!config.profanity_detection_enabled}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Language Detection */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Language Detection
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.language_detection_enabled}
                    onChange={(e) => handleChange('language_detection_enabled', e.target.checked)}
                  />
                }
                label="Enable Language Detection (Prefer English)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Flag products with non-English content for review
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Grammar Checking */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Grammar Checking
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.grammar_checking_enabled}
                    onChange={(e) => handleChange('grammar_checking_enabled', e.target.checked)}
                  />
                }
                label="Enable Grammar Quality Checking"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Grammar Quality Score"
                value={config.grammar_threshold}
                onChange={(e) => handleChange('grammar_threshold', parseFloat(e.target.value))}
                disabled={!config.grammar_checking_enabled}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ML Model Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon color="secondary" />
            <Typography variant="h6">Machine Learning Model</Typography>
            <Chip 
              label={config.ml_enabled ? "Enabled" : "Disabled"} 
              color={config.ml_enabled ? "success" : "default"}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.ml_enabled}
                    onChange={(e) => handleChange('ml_enabled', e.target.checked)}
                  />
                }
                label="Enable Machine Learning Predictions"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Use trained ML model to predict moderation decisions
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="ML Confidence Threshold"
                value={config.ml_confidence_threshold}
                onChange={(e) => handleChange('ml_confidence_threshold', parseFloat(e.target.value))}
                disabled={!config.ml_enabled}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                helperText="Threshold for ML-based auto-decisions"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Training Samples"
                value={config.ml_min_training_samples}
                onChange={(e) => handleChange('ml_min_training_samples', parseInt(e.target.value))}
                disabled={!config.ml_enabled}
                inputProps={{ min: 50, step: 10 }}
                helperText="Minimum completed moderations before training"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Automatic Model Training
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.ml_auto_training_enabled}
                    onChange={(e) => handleChange('ml_auto_training_enabled', e.target.checked)}
                    disabled={!config.ml_enabled}
                  />
                }
                label="Enable Automatic Retraining"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Training Interval (Days)"
                value={config.ml_training_interval_days}
                onChange={(e) => handleChange('ml_training_interval_days', parseInt(e.target.value))}
                disabled={!config.ml_auto_training_enabled || !config.ml_enabled}
                inputProps={{ min: 1, step: 1 }}
                helperText="Days between automatic model retraining"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.admin_override_learning_enabled}
                    onChange={(e) => handleChange('admin_override_learning_enabled', e.target.checked)}
                    disabled={!config.ml_enabled}
                  />
                }
                label="Learn from Admin Overrides"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Use admin override decisions to improve ML model accuracy
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Basic Validation Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Basic Validation Rules</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Text Validation */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Text Requirements
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Title Length"
                value={config.min_title_length}
                onChange={(e) => handleChange('min_title_length', parseInt(e.target.value))}
                inputProps={{ min: 1 }}
                helperText="Minimum number of characters in product title"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Description Length"
                value={config.min_description_length}
                onChange={(e) => handleChange('min_description_length', parseInt(e.target.value))}
                inputProps={{ min: 1 }}
                helperText="Minimum number of characters in description"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Image Validation */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Image Requirements
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Image Size (MB)"
                value={config.max_image_size_mb}
                onChange={(e) => handleChange('max_image_size_mb', parseFloat(e.target.value))}
                inputProps={{ min: 0.1, step: 0.5 }}
                helperText="Maximum allowed image file size"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Image Resolution"
                value={config.min_image_resolution}
                onChange={(e) => handleChange('min_image_resolution', parseInt(e.target.value))}
                inputProps={{ min: 100, step: 50 }}
                helperText="Minimum width or height in pixels"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Image Quality */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Image Quality Thresholds
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Brightness"
                value={config.min_brightness}
                onChange={(e) => handleChange('min_brightness', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 255 }}
                helperText="0-255"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Brightness"
                value={config.max_brightness}
                onChange={(e) => handleChange('max_brightness', parseInt(e.target.value))}
                inputProps={{ min: 0, max: 255 }}
                helperText="0-255"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Contrast"
                value={config.min_contrast}
                onChange={(e) => handleChange('min_contrast', parseInt(e.target.value))}
                inputProps={{ min: 0 }}
                helperText="Standard deviation"
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider /></Grid>

            {/* Penalty System */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Penalty System
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Penalties"
                value={config.max_penalties}
                onChange={(e) => handleChange('max_penalties', parseInt(e.target.value))}
                inputProps={{ min: 1 }}
                helperText="Maximum penalties before auto-rejection"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Auto-Approve Confidence"
                value={config.auto_approve_confidence}
                onChange={(e) => handleChange('auto_approve_confidence', parseFloat(e.target.value))}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                helperText="Confidence threshold for legacy auto-approval"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box 
        mt={{ xs: 3, sm: 4 }} 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="flex-end" 
        gap={{ xs: 1.5, sm: 2 }}
      >
        <Button
          variant="outlined"
          startIcon={!isMobile && <RefreshIcon />}
          onClick={fetchConfig}
          disabled={saving}
          size={isMobile ? "medium" : "large"}
          fullWidth={isMobile}
          sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
        >
          Reset Changes
        </Button>
        <Button
          variant="contained"
          size={isMobile ? "medium" : "large"}
          startIcon={!isMobile && <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          fullWidth={isMobile}
          sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
        >
          {saving ? 'Saving Configuration...' : 'Save All Changes'}
        </Button>
      </Box>
      </Container>
    </Box>
  );
};

export default ModerationConfig;
